import { useMemo } from "react";
import keyBy from "lodash.keyby";
import { LinkOutlined } from "@ant-design/icons";
import { useOutletContext } from "react-router-dom";
import { Badge, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";

import { PROFILE_TYPES_LABELS } from "@components/Domain";

import TableAmountColumn from "../TableAmountColumn";
import TableInvestmentColumn from "../TableInvestmentColumn";
import { getInvestmentTierCurrency } from "./getInvestedAmount";
import type { GetInvestmentAttachmentsCount } from "./useAttachmentsCount";
import type {
  SetState,
  Investment,
  CustomTarget,
  CustomAmounts,
  OutletContext,
  CustomAmountsMap,
  TransferInvestment,
} from "../types";

const LABEL_NAME = "Investment";
const LABEL_TYPE = "Profile Type";
const LABEL_CLASS = "Class";

const COLOR_MIGRATED = "#faad14";
const COLOR_SOURCE = "#52c41a";

type Options = {
  customAmounts: CustomAmounts[];
  customTargets: CustomTarget[];
  sourceProjectId: string;
  targetProjectId?: string;
  setCustomTargets: SetState<CustomTarget[]>;
  sourceInvestments: Investment[];
  targetInvestments: Investment[];
  getInvestmentAttachmentsCount: GetInvestmentAttachmentsCount;
};

const useColumns = ({
  customAmounts,
  customTargets,
  sourceProjectId,
  targetProjectId,
  setCustomTargets,
  sourceInvestments,
  targetInvestments,
  getInvestmentAttachmentsCount,
}: Options): ColumnsType<TransferInvestment> => {
  const { getProject } = useOutletContext<OutletContext>();

  const customAmountsMap = useMemo((): CustomAmountsMap =>
    keyBy(customAmounts, "investmentId")
  , [ customAmounts ]);

  const sourceProject = getProject(sourceProjectId);
  const targetProject = getProject(targetProjectId);

  const sourceProjectTierIds = useMemo(() =>
    sourceProject
      ? sourceProject.tiers.map(({ name }) => name)
      : []
  , [ sourceProject ]);

  const targetProjectTierIds = useMemo(() =>
    targetProject
      ? targetProject.tiers.map(({ name }) => name)
      : []
  , [ targetProject ]);

  const sourceProjectName = sourceProject?.name;
  const targetProjectName = targetProject?.name;

  const columns = useMemo((): ColumnsType<TransferInvestment> => {
    let projectTiersColumn: ColumnsType<TransferInvestment>[number] = {
      title: sourceProjectName,
      children: [
        ...(
          sourceProjectTierIds
            .map((tierId) => ({
              key: `${sourceProjectId}-${tierId}`,
              title: `${LABEL_CLASS} ${tierId}`,
              dataIndex: `${sourceProjectId}_${tierId}`,
              render: (_: unknown, investment: TransferInvestment) => getInvestmentTierCurrency(tierId, investment),
            }))
        ),
      ],
    };

    if (targetProjectId) {
      projectTiersColumn = {
        title: targetProjectName,
        children: [
          ...(
            targetProjectTierIds
              .map((tierId) => ({
                key: `${targetProjectId}-${tierId}`,
                title: `${LABEL_CLASS} ${tierId}`,
                dataIndex: `${targetProjectId}_${tierId}`,
                render: (_: unknown, { profileId, isIncluded, sourceInvestmentId, ...investment }: TransferInvestment) => {
                  return (
                    <TableAmountColumn
                      tierId={tierId}
                      isIncluded={isIncluded}
                      investment={investment}
                      customAmountsMap={customAmountsMap}
                      sourceInvestments={sourceInvestments}
                      sourceInvestmentId={sourceInvestmentId}
                    />
                  );
                },
              }))
          ),
        ],
      };
    }

    return [
      {
        title: "",
        children: [
          {
            key: "name",
            title: LABEL_NAME,
            dataIndex: "name",
            render: (value, { profileId, isIncluded, sourceInvestmentId, ...investment }) => {
              return (
                <TableInvestmentColumn
                  isIncluded={isIncluded}
                  investment={investment}
                  sourceProjectId={sourceProjectId}
                  targetInvestments={targetInvestments}
                  customTargets={customTargets}
                  setCustomTargets={setCustomTargets}
                />
              );
            },
          },
          {
            key: "profileType",
            title: LABEL_TYPE,
            dataIndex: "profileType",
            render: (value, investment) => {
              const { isIncluded } = investment;
              const type = PROFILE_TYPES_LABELS[value];

              if (!isIncluded) {
                return <Typography.Text type="secondary">{type}</Typography.Text>;
              }

              return type;
            },
          },
        ],
      },
      projectTiersColumn,
      {
        key: "attachmentsCount",
        align: "center",
        title: <LinkOutlined />,
        dataIndex: "_attachmentsCount",
        render: (_, investment) => {
          const value = getInvestmentAttachmentsCount(investment);

          if (!value) {
            return value;
          }

          const hasPlus = `${value}`.startsWith("+");

          if (hasPlus) {
            return <Badge count={value} color={COLOR_MIGRATED} />;
          }

          return <Badge count={value} color={COLOR_SOURCE} />;
        },
      },
      Table.EXPAND_COLUMN,
    ];
  }, [
    sourceProjectId,
    sourceProjectName,
    sourceProjectTierIds,
    sourceInvestments,
    targetProjectId,
    targetProjectName,
    targetProjectTierIds,
    targetInvestments,
    getInvestmentAttachmentsCount,
    customAmountsMap,
    customTargets,
    setCustomTargets,
  ]);

  return columns;
};

export default useColumns;
